import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Upload, X } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton, DsLabel } from "../../../design-system";
import { Info } from "lucide-react";
import api from "../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusWorkbook,
} from "../../Common/import-validation";
import {
  confirmWorkspaceAttImport,
  confirmWorkspaceEmpBulkExit,
  confirmWorkspaceEmpImport,
  confirmWorkspaceRolesImport,
  downloadWorkspaceIoTemplate,
  messageFromBlobDownloadError,
  runWorkspaceIoAction,
  validateWorkspaceAttImport,
  validateWorkspaceEmpBulkExit,
  validateWorkspaceEmpImport,
  validateWorkspaceRolesImport,
  WORKSPACE_IO_DEFS,
} from "./workspaceIoActions";

function countGrantedScreens(screenAccess) {
  if (!screenAccess || typeof screenAccess !== "object") return 0;
  let n = 0;
  for (const mod of Object.values(screenAccess)) {
    const screens = mod?.screens;
    if (!screens || typeof screens !== "object") continue;
    for (const row of Object.values(screens)) {
      if (row && typeof row === "object" && Object.values(row).some(Boolean)) n += 1;
    }
  }
  return n;
}
import {
  buildYearOptionsFromMin,
  clampYearMonthIndex,
  isMonthIndexBeforeMin,
  resolveCompanyMinDate,
} from "../../../utils/selectableDateBounds";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const EMP_SECTIONS = [
  { key: "org", label: "Org Details" },
  { key: "personal", label: "Personal Details" },
  { key: "family", label: "Family Details" },
  { key: "bank", label: "Bank Details" },
  { key: "qualification", label: "Education" },
  { key: "professional", label: "Profession" },
  { key: "statutory", label: "Statutory" },
];

const EMP_EXPORT_SECTIONS = [
  { key: "all", label: "All Employees" },
  ...EMP_SECTIONS,
];

/**
 * Standard Import / Export dialog used from the workspace hub.
 * Employee import uses shared ImportValidationReview (validate → consent → import).
 */
export default function WorkspaceIoModal({ featureId, isOpen, onClose }) {
  const def = WORKSPACE_IO_DEFS[featureId] || null;
  const fileRef = useRef(null);
  const now = useMemo(() => new Date(), []);
  const companyMinDate = useMemo(() => resolveCompanyMinDate(), [isOpen]);
  const initialPeriod = useMemo(() => {
    return clampYearMonthIndex(now.getFullYear(), now.getMonth(), companyMinDate);
  }, [now, companyMinDate]);
  const [month, setMonth] = useState(initialPeriod.month);
  const [year, setYear] = useState(initialPeriod.year);
  const [section, setSection] = useState("org");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultNote, setResultNote] = useState("");
  const [review, setReview] = useState(null);
  const [validatedPayload, setValidatedPayload] = useState(null);
  const [calendarOffConflicts, setCalendarOffConflicts] = useState([]);
  const [showCalendarOffConfirm, setShowCalendarOffConfirm] = useState(false);
  const [pendingIgnoreFailed, setPendingIgnoreFailed] = useState(false);
  const [rolesPreview, setRolesPreview] = useState([]);
  const [rolesPreviewLoading, setRolesPreviewLoading] = useState(false);
  const [rolesPreviewError, setRolesPreviewError] = useState("");
  const [exportRoleId, setExportRoleId] = useState("");

  const resetReview = () => {
    setReview(null);
    setValidatedPayload(null);
    setCalendarOffConflicts([]);
    setShowCalendarOffConfirm(false);
    setPendingIgnoreFailed(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setResultNote("");
    resetReview();
    setBusy(false);
    setRolesPreview([]);
    setRolesPreviewError("");
    setExportRoleId("");
    const period = clampYearMonthIndex(
      now.getFullYear(),
      now.getMonth(),
      companyMinDate
    );
    setMonth(period.month);
    setYear(period.year);
    setSection(featureId === "emp-export" ? "all" : "org");
    if (fileRef.current) fileRef.current.value = "";
  }, [isOpen, featureId, now, companyMinDate]);

  useEffect(() => {
    if (!isOpen || featureId !== "roles-export") return undefined;
    let cancelled = false;
    (async () => {
      setRolesPreviewLoading(true);
      setRolesPreviewError("");
      try {
        const [rolesRes, accessRes] = await Promise.all([
          api.get("/global-settings/roles"),
          api.get("/global-settings/screen-access").catch(() => null),
        ]);
        if (cancelled) return;
        const roles = Array.isArray(rolesRes?.data)
          ? rolesRes.data
          : Array.isArray(rolesRes?.data?.data)
            ? rolesRes.data.data
            : [];
        const accessByRole =
          accessRes?.data?.data && typeof accessRes.data.data === "object"
            ? accessRes.data.data
            : {};
        setRolesPreview(
          roles.map((r) => ({
            id: r.id,
            name: r.name || r.code,
            code: r.code,
            isSystem: !!(r.isSystem || r.is_system),
            active: r.active !== false,
            grantedScreens: countGrantedScreens(accessByRole[String(r.id)]),
          }))
        );
      } catch (err) {
        if (!cancelled) {
          setRolesPreviewError(
            err?.response?.data?.message || err?.message || "Could not load roles."
          );
        }
      } finally {
        if (!cancelled) setRolesPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, featureId]);

  const yearOptions = useMemo(
    () =>
      buildYearOptionsFromMin({
        minDate: companyMinDate,
        currentYear: now.getFullYear(),
        yearsBefore: 3,
        yearsAfter: 1,
      }),
    [companyMinDate, now]
  );

  const setPeriodYear = (nextYear) => {
    const period = clampYearMonthIndex(nextYear, month, companyMinDate);
    setYear(period.year);
    setMonth(period.month);
  };

  const setPeriodMonth = (nextMonth) => {
    const period = clampYearMonthIndex(year, nextMonth, companyMinDate);
    setYear(period.year);
    setMonth(period.month);
  };

  if (!isOpen || !def || typeof document === "undefined") return null;

  const ctx = {
    month,
    year,
    section,
    file,
    roleIds:
      featureId === "roles-export" && exportRoleId
        ? [exportRoleId]
        : featureId === "roles-export"
          ? []
          : undefined,
  };
  const isEmpImport = featureId === "emp-import";
  const isEmpBulkExit = featureId === "emp-bulk-exit";
  const isAttImport = featureId === "att-import";
  const isRolesImport = featureId === "roles-import";
  const isRolesExport = featureId === "roles-export";
  const usesReviewFlow = isEmpImport || isEmpBulkExit || isAttImport || isRolesImport;
  const showReview = usesReviewFlow && !!review;

  const handleDownloadTemplate = async () => {
    if (!def.hasTemplate) return;
    try {
      setBusy(true);
      await downloadWorkspaceIoTemplate(featureId, ctx);
      toast.success("Template downloaded");
    } catch (err) {
      const msg = await messageFromBlobDownloadError(err);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleValidateImport = async () => {
    if (!file) {
      toast.error("Please choose a file first.");
      return;
    }
    try {
      setBusy(true);
      setResultNote("");
      const payload = isRolesImport
        ? await validateWorkspaceRolesImport(ctx)
        : isAttImport
          ? await validateWorkspaceAttImport(ctx)
          : isEmpBulkExit
            ? await validateWorkspaceEmpBulkExit(ctx)
            : await validateWorkspaceEmpImport(ctx);
      const built = buildImportValidationResult({
        validRecords: payload.validRecords,
        invalidRecords: payload.invalidRecords,
        hardBlockImport: !!payload.hardBlockImport,
      });
      setValidatedPayload(payload);
      setReview(built);
      const conflicts = Array.isArray(payload.calendarOffConflicts)
        ? payload.calendarOffConflicts
        : [];
      setCalendarOffConflicts(conflicts);
      if (built.totalCount === 0) {
        toast.error("No data rows found in the file.");
      } else if (built.failedCount > 0) {
        toast(
          `Validated: ${built.successCount} successful, ${built.failedCount} failed`
        );
      } else if (isAttImport && conflicts.length > 0) {
        toast(
          `Validated OK — ${conflicts.length} day(s) differ from policy WO/PH. Confirm on Import.`
        );
      } else {
        toast.success(`All ${built.successCount} row(s) passed validation`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Validation failed";
      toast.error(String(msg).split("\n")[0] || "Validation failed");
      setResultNote(msg);
      resetReview();
    } finally {
      setBusy(false);
    }
  };

  const runAttImportConfirm = async ({
    ignoreFailed,
    overrideCalendarOff,
  }) => {
    if (!validatedPayload || !review) return;
    try {
      setBusy(true);
      const msg = await confirmWorkspaceAttImport(ctx, {
        ignoreFailed,
        successRows: review.successRows,
        overrideCalendarOff: !!overrideCalendarOff,
        clearCalendarOffConflicts: overrideCalendarOff
          ? []
          : calendarOffConflicts,
      });
      toast.success(msg || def.successToast || "Import completed");
      setResultNote(msg);
      resetReview();
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      if (def.closeOnSuccess !== false) onClose();
    } catch (err) {
      const data = err?.response?.data;
      const invalidRows = Array.isArray(data?.invalidRows) ? data.invalidRows : [];
      if (invalidRows.length > 0) {
        const failByCode = new Map();
        invalidRows.forEach((v) => {
          const code = String(v.employeeCode || v.empCode || "")
            .trim()
            .toLowerCase();
          if (!code) return;
          const reason = v.reason || v.status || "Validation failed";
          const prev = failByCode.get(code) || [];
          prev.push(reason);
          failByCode.set(code, prev);
        });
        const nextFailed = [];
        const nextSuccess = [];
        (review.allRows || []).forEach((row) => {
          const code = String(row.code || "")
            .trim()
            .toLowerCase();
          const reasons = failByCode.get(code);
          if (reasons?.length) {
            nextFailed.push({
              ...row,
              status: "failed",
              reason: [...new Set(reasons)].slice(0, 6).join("; "),
            });
          } else if (row.status === "success") {
            nextSuccess.push(row);
          }
        });
        failByCode.forEach((reasons, code) => {
          if (nextFailed.some((r) => String(r.code || "").toLowerCase() === code))
            return;
          nextFailed.push({
            id: `fail-be-${code}`,
            row: "—",
            code,
            name:
              invalidRows.find(
                (v) =>
                  String(v.employeeCode || "").toLowerCase() === code
              )?.name || code,
            status: "failed",
            reason: [...new Set(reasons)].slice(0, 6).join("; "),
            record: {},
            raw: {},
            fieldErrors: {},
          });
        });
        const rebuilt = {
          ...review,
          successRows: nextSuccess,
          failedRows: nextFailed,
          successCount: nextSuccess.length,
          failedCount: nextFailed.length,
          totalCount: nextSuccess.length + nextFailed.length,
          hardBlockImport: false,
          allRows: [...nextSuccess, ...nextFailed].sort(
            (a, b) => Number(a.row) - Number(b.row)
          ),
        };
        setReview(rebuilt);
        toast.error(
          `${nextFailed.length} employee(s) failed validation. Use Ignore failed & import, or fix the file and re-validate.`
        );
        setResultNote(data?.message || err?.message || "Import failed");
        return;
      }
      const msg = data?.message || err?.message || "Import failed";
      toast.error(msg);
      setResultNote(msg);
    } finally {
      setBusy(false);
      setShowCalendarOffConfirm(false);
    }
  };

  const handleConfirmImport = async ({ ignoreFailed }) => {
    if (!validatedPayload || !review) return;
    if (!ignoreFailed && review.failedCount > 0) {
      toast.error("Fix failed rows or choose Ignore failed & import.");
      return;
    }
    if (review.successCount <= 0) {
      toast.error("No successful rows to import.");
      return;
    }
    if (isAttImport && calendarOffConflicts.length > 0) {
      setPendingIgnoreFailed(!!ignoreFailed);
      setShowCalendarOffConfirm(true);
      return;
    }
    if (isAttImport) {
      await runAttImportConfirm({
        ignoreFailed,
        overrideCalendarOff: false,
      });
      return;
    }
    try {
      setBusy(true);
      let msg;
      if (isRolesImport) {
        msg = await confirmWorkspaceRolesImport(
          validatedPayload.roles || validatedPayload.validRecords || []
        );
      } else if (isEmpBulkExit) {
        msg = await confirmWorkspaceEmpBulkExit(validatedPayload.validRecords);
      } else {
        msg = await confirmWorkspaceEmpImport(
          validatedPayload.section || section,
          validatedPayload.validRecords
        );
      }
      toast.success(msg || def.successToast || "Import completed");
      setResultNote(msg);
      resetReview();
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      if (def.closeOnSuccess !== false) onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Import failed";
      toast.error(msg);
      setResultNote(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!review || !file) {
      toast.error("Validate a file first.");
      return;
    }
    try {
      setBusy(true);
      await downloadValidationStatusWorkbook(
        file,
        review.allRows,
        `${isAttImport ? "Attendance" : isEmpBulkExit ? "Bulk_Exit" : section || "Import"}_Validation_Status.xlsx`,
        isEmpImport || isEmpBulkExit ? { sheetName: "Import Data" } : {}
      );
      toast.success("Validation report downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download report");
    } finally {
      setBusy(false);
    }
  };

  const handlePrimary = async () => {
    if (usesReviewFlow) {
      await handleValidateImport();
      return;
    }
    if (def.needsFile && !file) {
      toast.error("Please choose a file first.");
      return;
    }
    try {
      setBusy(true);
      setResultNote("");
      const note = await runWorkspaceIoAction(featureId, ctx);
      const message = typeof note === "string" ? note : note?.message || null;
      if (message) setResultNote(message);
      toast.success(def.successToast || "Done");
      if (def.closeOnSuccess !== false) onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Action failed";
      toast.error(String(msg).split("\n")[0] || "Action failed");
      setResultNote(msg);
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    "w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ds-primary))]/30";

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="workspace-io-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy && !showCalendarOffConfirm) onClose();
      }}
    >
      <div
        className={`flex max-h-[min(92vh,720px)] w-full flex-col overflow-visible rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)] ${
          showReview ? "max-w-4xl" : "max-w-lg"
        }`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4 overflow-visible">
          <div className="min-w-0 overflow-visible">
            <div className="flex items-center gap-2">
              <h2 id="workspace-io-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
                {def.title}
              </h2>
              {(def.description || usesReviewFlow) && !showReview ? (
              <div className="group/info relative flex-shrink-0">
                <Info
                  size={15}
                  className="cursor-pointer text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                />

                <div className="pointer-events-none absolute left-full top-1/2 z-[9999] ml-2 hidden w-[400px] -translate-y-1/2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm leading-5 text-[hsl(var(--ds-fg))] shadow-lg group-hover/info:block">
                  {def.description ? <div>{def.description}</div> : null}

                  {usesReviewFlow ? (
                    <div className={def.description ? "mt-2" : ""}>
                      <strong>Validate first.</strong>{" "}
                      You will see successful and failed rows, then choose to import all
                      or import only the successful rows.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {showReview ? (
          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            {isAttImport && calendarOffConflicts.length > 0 ? (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Policy says <strong>WO/PH</strong> but Excel has a different status for{" "}
                <strong>{calendarOffConflicts.length}</strong> day
                {calendarOffConflicts.length === 1 ? "" : "s"}. This is still
                successful validation — you will be asked to confirm when you click
                Import.
              </div>
            ) : null}
            <ImportValidationReview
              result={review}
              busy={busy}
              entityLabel={isAttImport ? "employee" : "employee"}
              onBack={() => {
                resetReview();
                setResultNote("");
              }}
              onDownloadReport={handleDownloadReport}
              onImportAll={() => handleConfirmImport({ ignoreFailed: false })}
              onImportSuccessfulOnly={() =>
                handleConfirmImport({ ignoreFailed: true })
              }
            />
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {def.hasPeriod ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <DsLabel className="mb-1">Month</DsLabel>
                    <select
                      className={fieldClass}
                      value={month}
                      onChange={(e) => setPeriodMonth(Number(e.target.value))}
                      disabled={busy}
                    >
                      {MONTHS.map((m, idx) => (
                        <option
                          key={m}
                          value={idx}
                          disabled={isMonthIndexBeforeMin(year, idx, companyMinDate)}
                        >
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <DsLabel className="mb-1">Year</DsLabel>
                    <select
                      className={fieldClass}
                      value={year}
                      onChange={(e) => setPeriodYear(Number(e.target.value))}
                      disabled={busy}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}

              {def.hasSection ? (
                <div>
                  <DsLabel className="mb-1">Section</DsLabel>
                  <select
                    className={fieldClass}
                    value={section}
                    onChange={(e) => setSection(e.target.value)}
                    disabled={busy}
                  >
                    {(featureId === "emp-export"
                      ? EMP_EXPORT_SECTIONS
                      : EMP_SECTIONS
                    ).map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}


              {def.needsFile ? (
                <div>
                  <DsLabel className="mb-1">Select file</DsLabel>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    disabled={busy}
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null);
                      resetReview();
                      setResultNote("");
                    }}
                    className="block w-full text-sm text-[hsl(var(--ds-fg))] file:mr-3 file:rounded-md file:border-0 file:bg-[hsl(var(--ds-primary))] file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-[hsl(var(--ds-primary-fg))]"
                  />
                </div>
              ) : null}

              {isRolesExport ? (
                <div className="space-y-3">
                  <div>
                    <DsLabel className="mb-1">Role to export</DsLabel>
                    <select
                      className={fieldClass}
                      value={exportRoleId}
                      onChange={(e) => setExportRoleId(e.target.value)}
                      disabled={busy || rolesPreviewLoading}
                    >
                      <option value="">All roles</option>
                      {rolesPreview.map((r) => (
                        <option key={r.id} value={String(r.id)}>
                          {r.name} ({r.code}) · {r.grantedScreens} screens
                          {r.isSystem ? " · system" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  {rolesPreviewLoading ? (
                    <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading saved roles…</p>
                  ) : rolesPreviewError ? (
                    <p className="text-sm text-[hsl(var(--ds-danger))]">{rolesPreviewError}</p>
                  ) : (
                    <div className="max-h-48 overflow-auto rounded-lg border border-[hsl(var(--ds-border))]">
                      <table className="w-full text-left text-sm">
                        <thead className="sticky top-0 bg-[hsl(var(--ds-muted))] text-xs text-[hsl(var(--ds-muted-fg))]">
                          <tr>
                            <th className="px-3 py-2 font-semibold">Role</th>
                            <th className="px-3 py-2 font-semibold">Screens</th>
                            <th className="px-3 py-2 font-semibold">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(exportRoleId
                            ? rolesPreview.filter((r) => String(r.id) === String(exportRoleId))
                            : rolesPreview
                          ).map((r) => (
                            <tr
                              key={r.id}
                              className={`border-t border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] ${
                                String(exportRoleId) === String(r.id)
                                  ? "bg-[hsl(var(--ds-primary)/0.06)]"
                                  : ""
                              }`}
                            >
                              <td className="px-3 py-1.5 font-medium">
                                {r.name}
                                <span className="ml-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                                  {r.code}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 tabular-nums">{r.grantedScreens}</td>
                              <td className="px-3 py-1.5 text-[hsl(var(--ds-muted-fg))]">
                                {r.isSystem ? "System" : "Custom"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                    Export includes every granted action for the chosen role (or all). Edit RoleCode /
                    RoleName in Excel, then Import to create a new custom role or update an existing one.
                  </p>
                </div>
              ) : null}

              {resultNote ? (
                <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))] px-3 py-2 text-sm whitespace-pre-wrap text-[hsl(var(--ds-fg))]">
                  {resultNote}
                </div>
              ) : null}
            </div>
    
            <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
              {def.hasTemplate ? (
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={handleDownloadTemplate}
                  className="mr-auto gap-2"
                >
                  <Download size={16} />
                  Download Template
                </DsButton>
              ) : (
                <span className="mr-auto" />
              )}
              <DsButton
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={onClose}
              >
                Cancel
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={handlePrimary}
                className="gap-2"
              >
                {def.needsFile ? <Upload size={16} /> : <Download size={16} />}
                {busy
                  ? "Working…"
                  : usesReviewFlow
                    ? "Validate"
                    : def.primaryLabel}
              </DsButton>
            </div>
          </>
        )}
      </div>

      {showCalendarOffConfirm ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
          <div className="w-[min(96vw,560px)] rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-xl">
            <h3 className="ds-h4 text-amber-700">Weekly Off / Holiday conflict</h3>
            <p className="ds-body mt-2 text-[hsl(var(--ds-fg))]">
              Policy says <strong>WO/PH</strong>, but the Excel file has a different
              status for <strong>{calendarOffConflicts.length}</strong> day
              {calendarOffConflicts.length === 1 ? "" : "s"}. Continue to replace
              those days with the Excel statuses?
            </p>
            <div className="mt-3 max-h-40 overflow-auto rounded-lg border border-[hsl(var(--ds-border))] text-sm">
              <table className="w-full text-left">
                <thead className="bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]">
                  <tr>
                    <th className="px-2 py-1">Code</th>
                    <th className="px-2 py-1">Date</th>
                    <th className="px-2 py-1">Policy</th>
                    <th className="px-2 py-1">Excel</th>
                  </tr>
                </thead>
                <tbody>
                  {calendarOffConflicts.slice(0, 40).map((c, i) => (
                    <tr
                      key={`${c.employeeCode}-${c.date}-${i}`}
                      className="border-t border-[hsl(var(--ds-border))]"
                    >
                      <td className="px-2 py-1">{c.employeeCode}</td>
                      <td className="px-2 py-1">{c.date}</td>
                      <td className="px-2 py-1">{c.policyStatus}</td>
                      <td className="px-2 py-1">{c.importStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {calendarOffConflicts.length > 40 ? (
                <p className="px-2 py-1 text-[hsl(var(--ds-muted-fg))]">
                  +{calendarOffConflicts.length - 40} more
                </p>
              ) : null}
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <DsButton
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setShowCalendarOffConfirm(false)}
              >
                Cancel
              </DsButton>
              <DsButton
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() =>
                  runAttImportConfirm({
                    ignoreFailed: pendingIgnoreFailed,
                    overrideCalendarOff: false,
                  })
                }
              >
                No — keep policy WO/PH
              </DsButton>
              <DsButton
                type="button"
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={() =>
                  runAttImportConfirm({
                    ignoreFailed: pendingIgnoreFailed,
                    overrideCalendarOff: true,
                  })
                }
              >
                {busy ? "Importing…" : "Yes — use Excel statuses"}
              </DsButton>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
}
